import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { RouteCodeEnum } from '@models/menu/menuModel';
import { sharedFileRedirect } from '@module-instance/my-drive/common/utils/shared-file-route.helper';
import { menuItemResolver, TenantGuard, userResolver } from '@shared/guards';
import { DemoGuard } from '@shared/guards/demo.guard';
import { SysAdminGuard } from '@shared/guards/sys-admin.guard';

const routes: Routes = [
  //   Routes outside tenant context
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    path: 'tenant-list',
    canActivate: [MsalGuard],
    loadComponent: () => import('./system/tenant-list/tenant-list.component').then(c => c.TenantListComponent),
  },
  {
    path: 'login',
    loadComponent: () => import('./system/login/login.component').then(c => c.LoginComponent),
  },
  {
    path: 'postLogin',
    loadComponent: () => import('./system/post-login/post-login.component').then(c => c.PostLoginComponent),
  },
  {
    path: 'register',
    loadComponent: () => import('./system/register/register.component').then(c => c.RegisterComponent),
  },
  {
    path: '404',
    loadComponent: () => import('./system/not-found/not-found.component').then(c => c.NotFoundComponent),
  },
  {
    path: ':tenantId/shared_file',
    redirectTo: sharedFileRedirect,
  },

  {
    path: ':tenantId',
    canActivate: [TenantGuard],
    resolve: { userLoaded: userResolver },
    children: [
      { path: '', redirectTo: '/login', pathMatch: 'full' },
      {
        //kept for backward compatibility
        path: 'dashboard',
        redirectTo: 'personal-dashboard',
        pathMatch: 'full',
      },
      {
        path: 'personal-dashboard',
        loadComponent: () => import('./module/instance/dashboard/dashboard.component').then(c => c.DashboardComponent),
      },
      {
        path: 'contracts',
        resolve: { menuItem: menuItemResolver },
        loadComponent: () =>
          import('./module/instance/contracts/contracts-data-list/contracts-data-list.component').then(
            c => c.ContractsDataListComponent,
          ),
      },
      {
        path: 'holidays',
        loadComponent: () => import('./module/instance/holidays/holidays.component').then(c => c.HolidaysComponent),
      },
      {
        path: 'sys-admin',
        canActivate: [SysAdminGuard],
        loadChildren: () => import('./module/instance/sys-admin/sys-admin-routing').then(m => m.routes),
      },
      {
        path: 'lists-management',
        loadComponent: () => import('./module/instance/lists/lists.component').then(c => c.ListsComponent),
      },
      {
        path: 'organizations',
        loadComponent: () =>
          import('./module/instance/organizations/organizations.component').then(c => c.OrganizationsComponent),
        children: [
          {
            path: ':sid',
            loadComponent: () =>
              import('./module/instance/organizations/organizations-sid-outlet.component').then(
                c => c.OrganizationsSidOutletComponent,
              ),
          },
        ],
      },
      {
        path: 'external-contacts',
        loadComponent: () => import('./module/instance/contacts/contacts.component').then(c => c.ContactsComponent),
        children: [
          {
            path: ':sid',
            loadComponent: () =>
              import('./module/instance/contacts/contacts-sid-outlet.component').then(
                c => c.ContactsSidOutletComponent,
              ),
          },
        ],
      },
      {
        path: 'financials',
        loadComponent: () =>
          import('./module/instance/financial-schedules/financial-schedules.component').then(
            c => c.FinancialSchedulesComponent,
          ),
        children: [
          {
            path: ':sid',
            loadComponent: () =>
              import('./module/instance/financial-schedules/financial-schedules-sid-outlet.component').then(
                c => c.FinancialSchedulesSidOutletComponent,
              ),
          },
        ],
      },
      {
        path: 'menu',
        loadComponent: () =>
          import('./module/instance/menu-management/menu-management.component').then(c => c.MenuManagementComponent),
      },
      {
        path: 'modules',
        loadComponent: () => import('./module/instance/modules/modules.component').then(c => c.ModulesComponent),
      },
      {
        path: 'schedules',
        loadComponent: () =>
          import('./module/instance/work-schedules/work-schedules.component').then(c => c.WorkSchedulesComponent),
      },
      {
        path: 'users',
        loadComponent: () => import('./module/instance/users/users.component').then(c => c.UsersComponent),
        children: [
          {
            path: ':sid',
            loadComponent: () =>
              import('./module/instance/users/users-sid-outlet.component').then(c => c.UsersSidOutletComponent),
          },
        ],
      },
      {
        path: 'roles',
        loadComponent: () => import('./module/instance/roles/roles.component').then(c => c.RolesComponent),
      },
      {
        path: 'tenant-settings',
        loadComponent: () =>
          import('./module/instance/tenant-settings/tenant-settings-page/tenant-settings-page.component').then(
            c => c.TenantSettingsPageComponent,
          ),
      },
      {
        path: 'sov/:contractId',
        children: [
          {
            path: '',
            pathMatch: 'full',
            resolve: { menuItem: menuItemResolver },
            loadComponent: () =>
              import('./module/instance/sov/sov-data-list/sov-data-list.component').then(c => c.SovDataListComponent),
          },
          {
            path: ':entityInstanceId',
            resolve: { menuItem: menuItemResolver },
            loadComponent: () => import('./module/instance/sov/sov.component').then(c => c.SovComponent),
          },
        ],
      },

      {
        path: 'progresspayment/:contractId',
        children: [
          {
            path: '',
            pathMatch: 'full',
            resolve: { menuItem: menuItemResolver },
            loadComponent: () =>
              import('./module/instance/progress-payment/pp-data-list/pp-data-list.component').then(
                c => c.ProgressPaymentDataListComponent,
              ),
          },
        ],
      },
      /* Manual test route: rename progresspayment → fn in the URL to open the Angular pp-details page */
      {
        path: 'fn/:contractId',
        loadComponent: () =>
          import('./module/instance/progress-payment/progress-payment.components').then(
            c => c.ProgressPaymentComponent,
          ),
        children: [
          {
            path: ':periodId/:entityInstanceId',
            resolve: { menuItem: menuItemResolver },
            loadComponent: () =>
              import('./module/instance/progress-payment/pp-details/pp-details.component').then(
                c => c.PpDetailsComponent,
              ),
          },
        ],
      },
      {
        path: 'url/:moduleId',
        resolve: { menuItem: menuItemResolver },
        loadComponent: () =>
          import('./module/instance/iframe-url/iframe-url.component').then(c => c.IframeUrlComponent),
      },
      {
        path: 'reports',
        loadComponent: () =>
          import('./module/instance/power-bi-report/power-bi-report.component').then(c => c.PowerBiReportComponent),
      },
      {
        path: 'management-reports/:contractId/:moduleId',
        resolve: { menuItem: menuItemResolver },
        loadComponent: () =>
          import('./module/instance/power-bi-report/power-bi-report.component').then(c => c.PowerBiReportComponent),
      },
      {
        path: 'office',
        loadComponent: () => import('./module/instance/office/office.component').then(c => c.OfficeComponent),
      },
      {
        path: 'my-profile',
        loadComponent: () =>
          import('./module/instance/user-profile/user-profile.component').then(c => c.UserProfileComponent),
      },
      {
        path: 'company-profile',
        loadComponent: () =>
          import('./module/instance/company-profile/company-profile.component').then(c => c.CompanyProfileComponent),
      },
      {
        path: 'emails/:contractId/:folderId',
        resolve: { menuItem: menuItemResolver },
        loadComponent: () =>
          import('./module/instance/inbox/emails-data-list/emails-data-list.component').then(
            c => c.EmailsDataListComponent,
          ),
      },
      {
        path: 'emails/:contractId/:folderId/:emailId',
        resolve: { menuItem: menuItemResolver },
        loadComponent: () =>
          import('./module/instance/inbox/emails-data-list/emails-data-list.component').then(
            c => c.EmailsDataListComponent,
          ),
      },
      {
        path: 'my-drive',
        data: { menuCode: RouteCodeEnum.MyCIPODrive },
        resolve: { menuItem: menuItemResolver },
        loadComponent: () => import('./module/instance/my-drive/my-drive.component').then(c => c.MyDriveComponent),
      },
      {
        path: 'file-contract-manager/:entityInstanceId',
        resolve: { menuItem: menuItemResolver },
        loadComponent: () =>
          import('./module/instance/file-contract-manager/file-contract-manager.component').then(
            c => c.FileContractManagerComponent,
          ),
      },
      {
        path: '404',
        loadComponent: () => import('./system/not-found/not-found.component').then(c => c.NotFoundComponent),
      },
      {
        path: 'dm/:contractId/:moduleId',
        resolve: { menuItem: menuItemResolver },
        loadComponent: () =>
          import('./module/instance/dynamic-module/dynamic-module.component').then(c => c.DynamicModuleComponent),
        children: [
          {
            path: 'details/:moduleId/:urlActionId/:urlActionEntityId/:entityInstanceId/:screenId',
            loadComponent: () => import('./ng2/old-routes').then(c => c.OldRoutesRedirectComponent),
            data: { legacyRedirect: 'dm-details' },
          },
          {
            path: 'details/:urlActionId/:id/:screenId',
            loadComponent: () =>
              import('./module/instance/dynamic-module/document-details/details-page.component').then(
                c => c.DetailsPageComponent,
              ),
          },
        ],
      },
      {
        path: 'pdf-markup/:contractId',
        resolve: { menuItem: menuItemResolver },
        loadComponent: () =>
          import('./module/instance/pdf-markups/pdf-markups.component').then(c => c.PdfMarkupsComponent),
        children: [
          {
            path: ':d/:f/:n',
            loadComponent: () =>
              import('./module/instance/pdf-markups/pdf-markup-outlet.component').then(c => c.PdfMarkupOutletComponent),
          },
        ],
      },
      {
        path: 'demo',
        canActivate: [DemoGuard],
        loadChildren: () => import('./demo/demo-routing.module').then(m => m.DemoRoutingModule),
      },
      {
        path: '**',
        loadComponent: () => import('./ng2/angular-js/angular-js.component').then(c => c.AngularJsComponent),
      },
      //   Will implement this after having all routes in Angular
      //   {
      //     path: '**',
      //     loadComponent: () => import('./system/not-found/not-found.component').then(c => c.NotFoundComponent),
      //   },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
